/**
 * PlaybackSnapshot — Single source of truth for "what is currently playing?"
 *
 * All playback (library, smart playlists, Ella Radio) flows through maestroStore.
 * This module provides a normalized view of the current track and playback state.
 *
 * Imperative (for callbacks, timers, sync):
 *   isRadioActive()              — checks if Ella Radio session is active
 *   getActiveTrack()             — unified display track
 *   getCurrentPlaybackSnapshot() — full state for sync/serialization
 *
 * Reactive (for React components):
 *   useActiveTrack()             — subscribes to store, memoized
 *   useIsAnythingPlaying()       — boolean, for layout chrome
 */
import { useMemo } from 'react';
import { useMaestroStore } from '../store/maestroStore';

// =============================================================================
// SHARED TYPES
// =============================================================================

export interface DisplayTrack {
  id: string;
  title: string;
  artist: string;
  album: string;
  artUrl: string;
  duration: number;
  source: string;
  albumId?: string;
  trackId?: string;
  format?: string;
  bitDepth?: number;
  sampleRate?: number;
  formatId?: number;
  qobuzTrackId?: string;
}

export interface PlaybackSnapshot {
  track: DisplayTrack | null;
  playbackState: string;
  position: number;
  duration: number;
  activeSource: string;
  isRadio: boolean;
  volume: number;
  renderer: { id: string; name: string } | null;
  queue: any[];
  queueIndex: number;
  repeatMode: string;
  shuffled: boolean;
  shuffleOrder: any;
}

// =============================================================================
// SHARED PREDICATE
// =============================================================================

export function isRadioActive(): boolean {
  try {
    return sessionStorage.getItem('active_ella_radio') === 'true';
  } catch {
    return false;
  }
}

// =============================================================================
// TRACK NORMALIZATION
// =============================================================================

function fromLibraryTrack(t: any): DisplayTrack {
  return {
    id: t.id || '',
    title: t.title || '',
    artist: t.artist || '',
    album: t.album || '',
    artUrl: t.artworkUrl || t.artUrl || t.coverUrl || t.cover || t.imageUrl || t.artwork || '',
    duration: t.duration || 0,
    source: t.source || 'library',
    albumId: t.albumId || t.album_key || t.albumKey,
    trackId: t.id,
    format: t.format,
    bitDepth: t.bitDepth,
    sampleRate: t.sampleRate,
    formatId: t.formatId || 27,
    qobuzTrackId: t.qobuzTrackId || t.serviceTrackId || '',
  };
}

// =============================================================================
// IMPERATIVE API (callbacks, timers, sync, event handlers)
// =============================================================================

export function getActiveTrack(): DisplayTrack | null {
  const ms = useMaestroStore.getState();
  if (ms.currentTrack) return fromLibraryTrack(ms.currentTrack);
  return null;
}

export function getCurrentPlaybackSnapshot(): PlaybackSnapshot {
  const ms = useMaestroStore.getState();

  const track = ms.currentTrack ? fromLibraryTrack(ms.currentTrack) : null;
  const playbackState = ms.playbackState === 'loading' ? 'playing' : ms.playbackState;
  const position = ms.position;
  const duration = ms.duration;

  return {
    track,
    playbackState,
    position,
    duration,
    activeSource: ms.activeSource,
    isRadio: isRadioActive(),
    volume: ms.volume,
    renderer: ms.activeConnectorRenderer,
    queue: ms.queue,
    queueIndex: ms.queueIndex,
    repeatMode: ms.repeatMode,
    shuffled: ms.shuffled,
    shuffleOrder: ms.shuffleOrder,
  };
}

// =============================================================================
// REACT HOOKS (subscribe to store, memoized)
// =============================================================================

export function useActiveTrack(): DisplayTrack | null {
  const currentTrack = useMaestroStore((s) => s.currentTrack);

  return useMemo(() => {
    if (currentTrack) return fromLibraryTrack(currentTrack);
    return null;
  }, [currentTrack]);
}

export function useIsAnythingPlaying(): boolean {
  const currentTrack = useMaestroStore((s) => s.currentTrack);
  return !!currentTrack;
}
